import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Grid,
  TextField,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  CircularProgress,
  Checkbox,
  OutlinedInput,
  LinearProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  Code as CodeIcon,
  PlayArrow as PlayArrowIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import PageHeader from "../../components/common/PageHeader";
import AlertMessage from "../../components/common/AlertMessage";
import LoadingBackdrop from "../../components/common/LoadingBackdrop";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useAlert } from "../../context/AlertContext";
import api from "../../services/api";
import {
  Dataset,
  DatasetRequest,
  DatasetVariableRequest,
  Prompt,
  DatasetVariableWithName,
  ApiError,
  AssertionRequest,
  Assertion,
  Evaluation,
  Provider,
} from "../../types";
import { formatDateTime } from "../../utils/formatters";
import AssertionModal from "../../components/forms/AssertionModal";
import { SelectChangeEvent } from "@mui/material/Select";
import EvaluationResultsModal from "../../components/common/EvaluationResultsModal";
import PromptEditorModal from "./components/modals/PromptEditorModal";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dataset-tabpanel-${index}`}
      aria-labelledby={`dataset-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const DatasetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const alert = useAlert();
  const isNew = id === "new";

  // State for dataset, loading, and errors
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState<boolean>(!isNew);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);

  // State for prompts
  const [availablePrompts, setAvailablePrompts] = useState<Prompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [promptDialogOpen, setPromptDialogOpen] = useState<boolean>(false);

  // State for variables
  const [variableDialogOpen, setVariableDialogOpen] = useState<boolean>(false);
  const [selectedVariable, setSelectedVariable] =
    useState<DatasetVariableWithName | null>(null);
  const [selectedVariableType, setSelectedVariableType] = useState<string>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [templateVariables, setTemplateVariables] = useState<string[]>([]);
  const [variableNameInput, setVariableNameInput] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [promptForTemplateGroup, setPromptForTemplateGroup] =
    useState<boolean>(false);
  const [groupNameInput, setGroupNameInput] = useState<string>("");
  const [groupDescriptionInput, setGroupDescriptionInput] =
    useState<string>("");
  const [groupEditDialogOpen, setGroupEditDialogOpen] =
    useState<boolean>(false);
  const [groupDeleteDialogOpen, setGroupDeleteDialogOpen] =
    useState<boolean>(false);
  const [selectedGroupForDelete, setSelectedGroupForDelete] =
    useState<any>(null);

  // State for groups
  const [selectedGroupForEdit, setSelectedGroupForEdit] = useState<any>(null);
  const [currentGroupId, setCurrentGroupId] = useState<string>("");

  // State for processing
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // State for assertions
  const [selectedGroupForTests, setSelectedGroupForTests] = useState<
    string | null
  >(null);
  const [assertionsForGroup, setAssertionsForGroup] = useState<Assertion[]>([]);
  const [isCreatingAssertion, setIsCreatingAssertion] =
    useState<boolean>(false);
  const [isEditingAssertion, setIsEditingAssertion] = useState<boolean>(false);
  const [assertionToEdit, setAssertionToEdit] = useState<Assertion | null>(
    null,
  );

  // Add new state for prompt update
  const [addedVariables, setAddedVariables] = useState<string[]>([]);
  const [removedVariables, setRemovedVariables] = useState<string[]>([]);
  const [showPromptUpdateDialog, setShowPromptUpdateDialog] =
    useState<boolean>(false);
  const [affectedPrompts, setAffectedPrompts] = useState<Prompt[]>([]);

  // Add state for prompt editing modal
  const [editedPrompt, setEditedPrompt] = useState<{
    _id: string;
    name: string;
    prompt: string;
    type: "system" | "user" | "chat";
    description: string;
  } | null>(null);

  // Add these new state variables for tracking variable differences
  const [promptVarComparisons, setPromptVarComparisons] = useState<{
    [groupId: string]: {
      missingInGroup: string[]; // Variables in prompt but not in group
      extraInGroup: string[]; // Variables in group but not in prompt
    };
  }>({});

  // Evaluation execution state
  const [evaluationModalOpen, setEvaluationModalOpen] =
    useState<boolean>(false);
  const [executingEvaluation, setExecutingEvaluation] =
    useState<boolean>(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [evaluationId, setEvaluationId] = useState<string | null>(null);
  const [evaluationData, setEvaluationData] = useState<Evaluation | null>(null);
  const [evaluationStatus, setEvaluationStatus] = useState<
    "pending" | "running" | "completed" | "failed" | null
  >(null);
  const [evaluationPollingInterval, setEvaluationPollingInterval] =
    useState<NodeJS.Timeout | null>(null);

  // State for inline prompt editor testing
  const [promptEditorOpen, setPromptEditorOpen] = useState<boolean>(false);

  const [isRunningTests, setIsRunningTests] = useState<boolean>(false);
  const [testEvaluationData, setTestEvaluationData] = useState<any>(null);
  const [testEvaluationStatus, setTestEvaluationStatus] = useState<
    string | null
  >(null);
  const testEvaluationIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // State for executing all prompt tests at once
  const [executingAllTests, setExecutingAllTests] = useState<boolean>(false);

  // Handler for opening the prompt test editor
  const handleOpenPromptEditor = () => {
    if (selectedPrompt) {
      setEditedPrompt({
        _id: selectedPrompt._id,
        name: selectedPrompt.name,
        prompt: selectedPrompt.prompt,
        type: selectedPrompt.type as "system" | "user" | "chat",
        description: selectedPrompt.description || "",
      });
      setPromptEditorOpen(true);
    } else {
      alert.error("Please link a prompt to this test first");
    }
  };

  // Run tests with the modified prompt
  const handleRunPromptTests = async () => {
    if (!dataset || !selectedPrompt) {
      alert.error("A prompt must be linked to the dataset to run tests");
      return;
    }

    try {
      setIsRunningTests(true);

      // Create a temporary prompt with the modified content
      const tempPromptData = {
        ...selectedPrompt,
        prompt: editedPrompt?.prompt || "",
        response_format: selectedPrompt.response_format,
      };

      // Fetch all available providers
      let allProviderIds = [];
      const providersResponse = await api.providers.getAll();
      allProviderIds = providersResponse.data.map((provider) => provider._id);

      if (allProviderIds.length === 0) {
        alert.error("No providers available to run tests");
        setIsRunningTests(false);
        return;
      }

      // Get all variable group IDs
      const variableGroupIds = dataset.variableGroups.map((group) => group._id);

      // Create a single combined evaluation with the custom prompt
      const evaluationRequestData = {
        promptIds: [selectedPrompt._id],
        providerIds: allProviderIds,
        datasetId: dataset._id,
        variableGroupIds: variableGroupIds,
        customPrompt: tempPromptData, // Pass the modified prompt explicitly
      };

      // Use the combined evaluation endpoint
      const evaluationResponse = await api.evaluations.createCombined(
        evaluationRequestData,
      );
      const newEvaluationId = evaluationResponse.data._id;

      // Set the evaluation ID and start polling
      startTestEvaluationPolling(newEvaluationId);
    } catch (error) {
      setIsRunningTests(false);
      console.error("Error executing prompt tests:", error);
      alert.error("Failed to execute prompt tests");
    }
  };

  // Start polling for test evaluation updates
  const startTestEvaluationPolling = (evalId: string) => {
    // Clear any existing polling
    if (testEvaluationIntervalRef.current) {
      clearInterval(testEvaluationIntervalRef.current);
    }

    // Function to fetch evaluation status
    const pollEvaluation = async () => {
      try {
        const response = await api.evaluations.getById(evalId);
        const evaluation = response.data;

        if (!evaluation) {
          console.warn("Received null evaluation");
          return;
        }

        setTestEvaluationData(evaluation);
        setTestEvaluationStatus(evaluation.status);

        // If the evaluation is complete or failed, stop polling
        if (
          evaluation.status === "completed" ||
          evaluation.status === "failed"
        ) {
          if (testEvaluationIntervalRef.current) {
            clearInterval(testEvaluationIntervalRef.current);
            testEvaluationIntervalRef.current = null;
          }
          setIsRunningTests(false);
        }
      } catch (error) {
        console.error("Error polling test evaluation:", error);
        if (testEvaluationIntervalRef.current) {
          clearInterval(testEvaluationIntervalRef.current);
          testEvaluationIntervalRef.current = null;
        }
        setIsRunningTests(false);
      }
    };

    // Initial poll
    pollEvaluation();

    // Set up interval polling (every 2 seconds)
    const intervalId = setInterval(pollEvaluation, 2000);
    testEvaluationIntervalRef.current = intervalId;
  };

  // Add this function to compare variables between prompt and groups
  const comparePromptAndGroupVariables = useCallback(() => {
    if (!dataset?.variableGroups || !templateVariables.length) {
      setPromptVarComparisons({});
      return;
    }

    const comparisons: {
      [groupId: string]: {
        missingInGroup: string[];
        extraInGroup: string[];
      };
    } = {};

    // For each variable group, compare its variables with template variables
    dataset.variableGroups.forEach((group) => {
      const groupVarNames = Object.keys(group.variables || {});

      // Find variables in prompt but not in group
      const missingInGroup = templateVariables.filter(
        (varName) => !groupVarNames.includes(varName),
      );

      // Find variables in group but not in prompt
      const extraInGroup = groupVarNames.filter(
        (varName) => !templateVariables.includes(varName),
      );

      comparisons[group._id] = {
        missingInGroup,
        extraInGroup,
      };
    });

    setPromptVarComparisons(comparisons);
  }, [dataset?.variableGroups, templateVariables]);

  // Call the comparison function when relevant data changes
  useEffect(() => {
    comparePromptAndGroupVariables();
  }, [comparePromptAndGroupVariables]);

  // Add function to add a missing variable to a group
  const handleAddMissingVariable = async (
    groupId: string,
    variableName: string,
  ) => {
    if (!dataset) return;

    setIsProcessing(true);
    try {
      // Create a basic variable with the template name
      await api.variableGroups.createVariable(dataset._id, groupId, {
        name: variableName,
        value: "",
        description: `Auto-added from prompt "${selectedPrompt?.name || ""}"`,
      });

      alert.success(`Variable '${variableName}' added to group`);
      await fetchDataset(); // Refresh data

      // Update comparisons
      const updatedComparisons = { ...promptVarComparisons };
      const missingVars =
        updatedComparisons[groupId]?.missingInGroup.filter(
          (v) => v !== variableName,
        ) || [];
      updatedComparisons[groupId] = {
        ...updatedComparisons[groupId],
        missingInGroup: missingVars,
      };
      setPromptVarComparisons(updatedComparisons);
    } catch (error) {
      console.error("Error adding variable:", error);
      alert.error(
        `Failed to add variable: ${(error as ApiError).customMessage || "Unknown error"}`,
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Add function to remove an extra variable from a group
  const handleRemoveExtraVariable = async (
    groupId: string,
    variableName: string,
  ) => {
    if (!dataset) return;

    setIsProcessing(true);
    try {
      await api.variableGroups.deleteVariable(
        dataset._id,
        groupId,
        variableName,
      );

      alert.success(`Variable '${variableName}' removed from group`);
      await fetchDataset(); // Refresh data

      // Update comparisons
      const updatedComparisons = { ...promptVarComparisons };
      const extraVars =
        updatedComparisons[groupId]?.extraInGroup.filter(
          (v) => v !== variableName,
        ) || [];
      updatedComparisons[groupId] = {
        ...updatedComparisons[groupId],
        extraInGroup: extraVars,
      };
      setPromptVarComparisons(updatedComparisons);
    } catch (error) {
      console.error("Error removing variable:", error);
      alert.error(
        `Failed to remove variable: ${(error as ApiError).customMessage || "Unknown error"}`,
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Add function to sync all variables in a group with the prompt
  const handleSyncGroupWithPrompt = async (groupId: string) => {
    if (!dataset || !promptVarComparisons[groupId]) return;

    setIsProcessing(true);
    try {
      const { missingInGroup, extraInGroup } = promptVarComparisons[groupId];

      // Add all missing variables
      for (const varName of missingInGroup) {
        await api.variableGroups.createVariable(dataset._id, groupId, {
          name: varName,
          value: "",
          description: `Auto-added from prompt "${selectedPrompt?.name || ""}"`,
        });
      }

      // Remove all extra variables
      for (const varName of extraInGroup) {
        await api.variableGroups.deleteVariable(dataset._id, groupId, varName);
      }

      alert.success(`Group synchronized with prompt variables`);
      await fetchDataset(); // Refresh data

      // Update comparisons
      const updatedComparisons = { ...promptVarComparisons };
      updatedComparisons[groupId] = {
        missingInGroup: [],
        extraInGroup: [],
      };
      setPromptVarComparisons(updatedComparisons);
    } catch (error) {
      console.error("Error synchronizing group:", error);
      alert.error(
        `Failed to synchronize group: ${(error as ApiError).customMessage || "Unknown error"}`,
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Modify the variable form submit to handle group variables
  const handleVariableFormSubmit = async (values: DatasetVariableRequest) => {
    if (!dataset) return;

    setIsProcessing(true);
    try {
      if (currentGroupId) {
        // Working with a variable in a group
        const group = dataset.variableGroups.find(
          (g) => g._id === currentGroupId,
        );
        if (!group) throw new Error("Group not found");

        // Get existing variable names in this group
        const existingVariables = Object.keys(group.variables || {});

        if (selectedVariable) {
          // Variable name is now the key in the variables object
          // If name changes, it's like deleting old key and creating new one
          await api.variableGroups.updateVariable(
            dataset._id,
            currentGroupId,
            selectedVariable.name, // Use the original name as the key to update
            values,
          );

          // Check if the variable name changed
          if (selectedVariable.name !== values.name) {
            // This is a rename operation - equivalent to removing old and adding new
            await checkForVariableChanges(
              currentGroupId,
              [
                ...existingVariables.filter((v) => v !== selectedVariable.name),
                values.name,
              ],
              existingVariables,
            );
          }
        } else {
          // Create new variable with name as the key
          await api.variableGroups.createVariable(
            dataset._id,
            currentGroupId,
            values,
          );

          // Check for added variable
          await checkForVariableChanges(
            currentGroupId,
            [...existingVariables, values.name],
            existingVariables,
          );
        }

        alert.success(
          selectedVariable
            ? "Variable updated successfully"
            : "Variable added successfully",
        );
        setVariableDialogOpen(false);
        setSelectedVariable(null);
        await fetchDataset(); // Refresh to show updated variables
      } else {
        // Legacy behavior - leaving for backward compatibility if needed
        if (selectedVariable) {
          await api.datasetVariables.update(
            dataset._id,
            selectedVariableType,
            selectedVariable._id,
            values,
          );
          alert.success("Variable updated successfully");
        } else {
          await api.datasetVariables.create(
            dataset._id,
            selectedVariableType,
            values,
          );
          alert.success("Variable added successfully");
        }
        setVariableDialogOpen(false);
        setSelectedVariable(null);
        await fetchDataset();
      }
    } catch (error) {
      console.error("Error submitting variable form:", error);
      alert.error("Failed to save variable");
    } finally {
      setIsProcessing(false);
    }
  };

  // Initialize formik for dataset
  const formik = useFormik<DatasetRequest>({
    initialValues: {
      name: dataset?.name || "",
      description: dataset?.description || "",
      promptId: dataset?.promptId || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      description: Yup.string(),
      promptId: Yup.string(),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        if (isNew) {
          const response = await api.datasets.create(values);
          alert.success("Prompt Test created successfully");
          navigate(`/datasets/${response.data._id}`);
        } else if (dataset) {
          await api.datasets.update(dataset._id, values);
          alert.success("Prompt Test updated successfully");
          fetchDataset();
        }
      } catch (error) {
        console.error("Error saving dataset:", error);
        alert.error("Failed to save prompt test");
      } finally {
        setSaving(false);
      }
    },
  });

  // Initialize formik for variable form
  const variableFormik = useFormik<DatasetVariableRequest>({
    initialValues: {
      name: selectedVariable?.name || "",
      value: selectedVariable?.value || "",
      description: selectedVariable?.description || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      value: Yup.string().required("Value is required"),
      description: Yup.string(),
    }),
    enableReinitialize: true,
    onSubmit: handleVariableFormSubmit,
  });

  // Fetch dataset if editing
  useEffect(() => {
    if (isNew) return;
    fetchDataset();
  }, [id, isNew]);

  // Fetch all prompts
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const response = await api.prompts.getAll();
        setAvailablePrompts(response.data);
      } catch (error) {
        console.error("Error fetching prompts:", error);
      }
    };

    fetchPrompts();
  }, []);

  // Extract template variables when prompt changes
  useEffect(() => {
    if (!selectedPrompt) return;

    // Find variables in format {{variable_name}}
    const regex = /\{\{([^}]+)\}\}/g;
    // Fix the iterator issue by using a different approach
    const matches: string[] = [];
    let match;
    while ((match = regex.exec(selectedPrompt.prompt)) !== null) {
      matches.push(match[1]);
    }

    // Remove duplicates using filter instead of Set
    const variables = matches.filter(
      (value, index, self) => self.indexOf(value) === index,
    );

    setTemplateVariables(variables);
  }, [selectedPrompt]);

  // Add after the templateVariables useEffect
  useEffect(() => {
    if (dataset && dataset.variableGroups) {
      // Set the initial tab to the first variable group if available
      if (dataset.variableGroups.length > 0 && tabValue === 0) {
        setTabValue(0);

        // Load assertions for the initially selected group
        const groupId = dataset.variableGroups[0]._id;
        loadAssertionsForGroup(groupId);
      }
    }
  }, [dataset]);

  // Find the modified fetchDataset function and restore it to its original form
  const fetchDataset = async () => {
    try {
      setLoading(true);
      const response = await api.datasets.getById(id as string);
      setDataset(response.data);

      // If dataset has a promptId, fetch the prompt
      if (response.data.promptId) {
        const promptResponse = await api.prompts.getById(
          response.data.promptId,
        );
        setSelectedPrompt(promptResponse.data);
      } else {
        setSelectedPrompt(null);
      }

      setError(null);
    } catch (error) {
      console.error("Error fetching dataset:", error);
      setError("Failed to load prompt test. Please try again.");
      alert.error("Failed to fetch prompt test");
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);

    // Load assertions for the selected group
    if (dataset?.variableGroups && dataset.variableGroups[newValue]) {
      const groupId = dataset.variableGroups[newValue]._id;
      loadAssertionsForGroup(groupId);
    }
  };

  // Open prompt selection dialog
  const handleOpenPromptDialog = () => {
    setPromptDialogOpen(true);
  };

  // Close prompt selection dialog
  const handleClosePromptDialog = () => {
    setPromptDialogOpen(false);
  };

  // Select a prompt to associate with the dataset
  const handlePromptSelect = async (promptId: string) => {
    if (!dataset) return;

    try {
      setIsProcessing(true);
      await api.datasets.linkPrompt(dataset._id, promptId);
      alert.success("Prompt linked to prompt test successfully");
      fetchDataset();
    } catch (error) {
      console.error("Error linking prompt:", error);
      alert.error("Failed to link prompt to prompt test");
    } finally {
      setIsProcessing(false);
    }
  };

  // Unlink prompt from dataset
  const handlePromptUnlink = async () => {
    if (!dataset) return;

    try {
      setIsProcessing(true);
      await api.datasets.unlinkPrompt(dataset._id);
      alert.success("Prompt unlinked from prompt test successfully");
      setSelectedPrompt(null);
      fetchDataset();
    } catch (error) {
      console.error("Error unlinking prompt:", error);
      alert.error("Failed to unlink prompt from prompt test");
    } finally {
      setIsProcessing(false);
    }
  };

  // Close variable dialog
  const handleCloseVariableDialog = () => {
    setVariableDialogOpen(false);
    setSelectedVariable(null);
  };

  // Confirm delete variable
  const confirmDeleteVariable = async () => {
    if (!dataset || !selectedVariable) return;

    setIsProcessing(true);
    try {
      if (currentGroupId) {
        // Get group and its variables before deletion
        const group = dataset.variableGroups.find(
          (g) => g._id === currentGroupId,
        );
        if (!group) throw new Error("Group not found");

        const existingVariables = Object.keys(group.variables || {});

        // Delete variable from a group - now using variable name (key) instead of _id
        await api.variableGroups.deleteVariable(
          dataset._id,
          currentGroupId,
          selectedVariable.name, // Use the name as the identifier
        );

        // Check for variable changes (this is a removal)
        await checkForVariableChanges(
          currentGroupId,
          existingVariables.filter((v) => v !== selectedVariable.name),
          existingVariables,
        );
      } else {
        // Legacy behavior for dataset-level variables (array-based)
        await api.datasetVariables.delete(
          dataset._id,
          selectedVariableType,
          selectedVariable._id,
        );
      }

      alert.success("Variable deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedVariable(null);
      setCurrentGroupId(""); // Changed from null to empty string
      fetchDataset();
    } catch (error) {
      console.error("Error deleting variable:", error);
      alert.error("Failed to delete variable");
    } finally {
      setIsProcessing(false);
    }
  };

  // Add after fetchDataset function
  const handleAddAllTemplateVariables = () => {
    if (!dataset || !selectedPrompt || templateVariables.length === 0) return;

    // Open a dialog to create a new variable group with all template variables
    setPromptForTemplateGroup(true);
  };

  const handleCreateVariableGroup = async (name: string, description = "") => {
    if (!dataset) return;

    setIsProcessing(true);
    try {
      // Create a new variable group with the template variables
      await api.variableGroups.create(dataset._id, {
        name,
        description,
        templateVariables,
      });

      alert.success("Prompt Dataset created successfully");
      setPromptForTemplateGroup(false);
      fetchDataset(); // Refresh the dataset with the new group
    } catch (error) {
      console.error("Error creating variable group:", error);
      alert.error("Failed to create prompt dataset");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditGroup = (group: any) => {
    setGroupNameInput(group.name);
    setGroupDescriptionInput(group.description || "");
    setSelectedGroupForEdit(group);
    setGroupEditDialogOpen(true);
  };

  const handleUpdateGroup = async () => {
    if (!dataset || !selectedGroupForEdit) return;

    setIsProcessing(true);
    try {
      await api.variableGroups.update(dataset._id, selectedGroupForEdit._id, {
        name: groupNameInput,
        description: groupDescriptionInput,
      });

      alert.success("Prompt Dataset updated successfully");
      setGroupEditDialogOpen(false);
      fetchDataset();
    } catch (error) {
      console.error("Error updating variable group:", error);
      alert.error("Failed to update prompt dataset");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteGroup = (group: any) => {
    setSelectedGroupForDelete(group);
    setGroupDeleteDialogOpen(true);
  };

  const confirmDeleteGroup = async () => {
    if (!dataset || !selectedGroupForDelete) return;

    setIsProcessing(true);
    try {
      await api.variableGroups.delete(dataset._id, selectedGroupForDelete._id);

      alert.success("Prompt Dataset deleted successfully");
      setGroupDeleteDialogOpen(false);
      setSelectedGroupForDelete(null);
      fetchDataset();
    } catch (error) {
      console.error("Error deleting variable group:", error);
      alert.error("Failed to delete prompt dataset");
    } finally {
      setIsProcessing(false);
    }
  };

  // Add a quick variable to a group
  const handleAddVariableTypeToGroup = async (groupId: string) => {
    if (!dataset || !variableNameInput.trim()) return;

    setIsProcessing(true);
    try {
      // Get group and its variables before addition
      const group = dataset.variableGroups.find((g) => g._id === groupId);
      if (!group) throw new Error("Group not found");

      const existingVariables = Object.keys(group.variables || {});

      // Create a basic variable with just a name and empty string value
      await api.variableGroups.createVariable(dataset._id, groupId, {
        name: variableNameInput.trim(),
        value: "",
        description: "",
      });

      // Check for variable changes (this is an addition)
      await checkForVariableChanges(
        groupId,
        [...existingVariables, variableNameInput.trim()],
        existingVariables,
      );

      alert.success(`Variable '${variableNameInput}' added successfully`);
      setVariableNameInput(""); // Clear the input field
      fetchDataset(); // Refresh data
    } catch (error) {
      console.error("Error adding variable:", error);
      alert.error(
        `Failed to add variable: ${(error as ApiError).customMessage || "Unknown error"}`,
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenGroupVariableDialog = (
    groupId: string,
    variableName: string | null = null,
  ) => {
    setCurrentGroupId(groupId);
    setSelectedVariableType(""); // No longer needed with new structure

    if (variableName) {
      // For editing existing variable
      const group = dataset?.variableGroups.find((g) => g._id === groupId);
      if (!group) return;

      const variable = group.variables[variableName];
      if (!variable) return;

      // Create a DatasetVariableWithName object for the form
      const variableWithName: DatasetVariableWithName = {
        ...variable,
        name: variableName, // Add the name (key) to the variable object
      };

      setSelectedVariable(variableWithName);
      variableFormik.setValues({
        name: variableName,
        value: variable.value,
        description: variable.description || "",
      });
    } else {
      // For creating a new variable
      setSelectedVariable(null);
      variableFormik.resetForm();
    }

    setVariableDialogOpen(true);
  };

  const handleDeleteGroupVariable = (groupId: string, variableName: string) => {
    setCurrentGroupId(groupId);
    setSelectedVariableType(""); // No longer needed

    // Get the variable object from the group
    const group = dataset?.variableGroups.find((g) => g._id === groupId);
    if (!group) return;

    const variable = group.variables[variableName];
    if (!variable) return;

    // Create a DatasetVariableWithName for consistency
    const variableWithName: DatasetVariableWithName = {
      ...variable,
      name: variableName,
    };

    setSelectedVariable(variableWithName);
    setDeleteDialogOpen(true);
  };

  // Add this function to handle deleting legacy variables
  const handleDeleteLegacyVariables = async () => {
    if (!dataset) return;

    setIsProcessing(true);
    try {
      // Create an empty variables object to remove all legacy variables
      await api.datasets.updateVariables(dataset._id, {});
      alert.success("Legacy variables removed successfully");
      fetchDataset();
    } catch (error) {
      console.error("Error removing legacy variables:", error);
      alert.error("Failed to remove legacy variables");
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler to load assertions for a specific group
  const loadAssertionsForGroup = async (groupId: string) => {
    if (!dataset) return;

    setSelectedGroupForTests(groupId);

    try {
      // Fetch assertions for this group
      const response = await api.variableGroups.getAssertions(
        dataset._id,
        groupId,
      );
      if (response.data) {
        setAssertionsForGroup(response.data);
      }
    } catch (error) {
      console.error("Error fetching assertions for group:", error);
      alert.error("Failed to fetch assertions for this dataset");
    }
  };

  // Handler to edit an assertion
  const handleEditAssertion = (assertion: Assertion) => {
    setAssertionToEdit(assertion);
    setIsEditingAssertion(true);
    setIsCreatingAssertion(true);
  };

  // Get assertion type label
  const getAssertionTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      equals: "Equals",
      contains: "Contains",
      matches: "Matches",
      similar: "Similar",
      "is-json": "Is JSON",
      "llm-rubric": "LLM Rubric",
      javascript: "JavaScript",
    };

    return typeMap[type] || type;
  };

  // Function to check which prompts use a particular variable
  const findPromptsUsingVariables = async (variableNames: string[]) => {
    if (!variableNames.length) return [];

    try {
      const response = await api.prompts.getAll();
      const allPrompts = response.data;

      // Filter prompts that contain any of the specified variables
      return allPrompts.filter((prompt) => {
        for (const varName of variableNames) {
          if (prompt.prompt.includes(`{{${varName}}}`)) {
            return true;
          }
        }
        return false;
      });
    } catch (error) {
      console.error("Error finding affected prompts:", error);
      return [];
    }
  };

  // Function to check for added/removed variables
  const checkForVariableChanges = async (
    groupId: string,
    newVariables: string[],
    oldVariables: string[] = [],
  ) => {
    // Find added and removed variables
    const added = newVariables.filter((v) => !oldVariables.includes(v));
    const removed = oldVariables.filter((v) => !newVariables.includes(v));

    setAddedVariables(added);
    setRemovedVariables(removed);

    if (added.length > 0 || removed.length > 0) {
      // Find prompts that are affected by these variable changes
      const affected = await findPromptsUsingVariables([...added, ...removed]);
      setAffectedPrompts(affected);

      if (affected.length > 0) {
        setShowPromptUpdateDialog(true);
      }
    }
  };

  // Function to handle saving the edited prompt
  const handleSavePromptEdit = async () => {
    if (!editedPrompt) return;

    try {
      await api.prompts.update(editedPrompt._id, {
        name: editedPrompt.name,
        prompt: editedPrompt.prompt,
        type: editedPrompt.type,
        description: editedPrompt.description,
      });

      // Update local state
      if (selectedPrompt) {
        setSelectedPrompt({
          ...selectedPrompt,
          name: editedPrompt.name,
          prompt: editedPrompt.prompt,
          type: editedPrompt.type,
          description: editedPrompt.description,
        });
      }

      // Extract template variables from the updated prompt
      const regex = /\{\{([^}]+)\}\}/g;
      const matches: string[] = [];
      let match;
      while ((match = regex.exec(editedPrompt.prompt)) !== null) {
        matches.push(match[1]);
      }

      // Remove duplicates
      const variables = matches.filter(
        (value, index, self) => self.indexOf(value) === index,
      );
      setTemplateVariables(variables);

      // Trigger comparison update
      setTimeout(() => {
        comparePromptAndGroupVariables();
      }, 100);

      alert.success("Prompt updated successfully");
    } catch (error) {
      console.error("Error updating prompt:", error);
      alert.error("Failed to update prompt. Please try again.");
    }
  };

  // Handler to remove an assertion from the dataset
  const handleDeleteAssertion = async (assertionId: string) => {
    if (!dataset || !selectedGroupForTests) return;

    setIsProcessing(true);
    try {
      // Remove the assertion from the group
      await api.variableGroups.removeAssertion(
        dataset._id,
        selectedGroupForTests,
        assertionId,
      );

      // Refresh assertions for this group
      const response = await api.variableGroups.getAssertions(
        dataset._id,
        selectedGroupForTests,
      );

      if (response.data) {
        setAssertionsForGroup(response.data);
      }

      alert.success("Assertion deleted successfully");

      // No need to call fetchDataset() as we've already refreshed the assertions
    } catch (error) {
      console.error("Error deleting assertion:", error);
      alert.error("Failed to delete assertion");
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle modal close
  const handleCloseAssertionModal = () => {
    // Reset state
    setIsCreatingAssertion(false);
    setIsEditingAssertion(false);
    setAssertionToEdit(null);

    // Refresh assertions if we have a selected group
    if (currentGroupId) {
      loadAssertionsForGroup(currentGroupId);
    }
  };

  // Fetch providers for execution modal
  const fetchProviders = async () => {
    try {
      const response = await api.providers.getAll();
      setProviders(response.data);

      // Select first provider by default if no providers selected
      if (selectedProviderIds.length === 0 && response.data.length > 0) {
        setSelectedProviderIds([response.data[0]._id]);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
      alert.error("Failed to fetch providers");
    }
  };

  // Execute evaluation based on current settings
  const handleExecuteEvaluation = async () => {
    if (!dataset || !selectedPrompt) {
      alert.error(
        "A prompt must be linked to the dataset to run an evaluation",
      );
      return;
    }

    if (selectedProviderIds.length === 0) {
      alert.error("At least one provider must be selected");
      return;
    }

    try {
      setExecutingEvaluation(true);

      // Get current variable group
      const currentGroup = dataset.variableGroups[tabValue];

      // Create evaluation directly using the prompt ID and provider IDs
      const evaluationRequestData = {
        testIds: [], // Empty array to satisfy the type requirement
        promptIds: [selectedPrompt._id],
        providerIds: selectedProviderIds,
        // Instead of creating new test variables, pass the dataset and group information
        datasetId: dataset._id,
        variableGroupId: currentGroup._id,
      };

      const evaluationResponse = await api.evaluations.create(
        evaluationRequestData,
      );
      const newEvaluationId = evaluationResponse.data._id;

      setEvaluationId(newEvaluationId);
      setEvaluationModalOpen(true);

      // Start polling for evaluation updates
      startPollingEvaluation(newEvaluationId);
    } catch (error) {
      console.error("Error executing evaluation:", error);
      alert.error("Failed to execute evaluation");
      setExecutingEvaluation(false);
    }
  };

  // Start polling for evaluation updates
  const startPollingEvaluation = (evalId: string) => {
    // Clear any existing polling
    if (evaluationPollingInterval) {
      clearInterval(evaluationPollingInterval);
    }

    // Function to fetch evaluation status
    const pollEvaluation = async () => {
      try {
        const response = await api.evaluations.getById(evalId);
        const evaluation = response.data;

        setEvaluationData(evaluation);
        setEvaluationStatus(evaluation.status);

        // If the evaluation is complete or failed, stop polling
        if (
          evaluation.status === "completed" ||
          evaluation.status === "failed"
        ) {
          if (evaluationPollingInterval) {
            clearInterval(evaluationPollingInterval);
            setEvaluationPollingInterval(null);
          }
          setExecutingEvaluation(false);
        }
      } catch (error) {
        console.error("Error polling evaluation:", error);
        if (evaluationPollingInterval) {
          clearInterval(evaluationPollingInterval);
          setEvaluationPollingInterval(null);
        }
        setExecutingEvaluation(false);
      }
    };

    // Initial poll
    pollEvaluation();

    // Set up interval polling (every 2 seconds)
    const intervalId = setInterval(pollEvaluation, 2000);
    setEvaluationPollingInterval(intervalId);
  };

  // Handle closing the evaluation modal
  const handleCloseEvaluationModal = () => {
    // Clear any active polling
    if (evaluationPollingInterval) {
      clearInterval(evaluationPollingInterval);
      setEvaluationPollingInterval(null);
    }

    setEvaluationModalOpen(false);
    setEvaluationId(null);
    setEvaluationData(null);
    setEvaluationStatus(null);
    setExecutingEvaluation(false);
  };

  // Handle change of selected providers
  const handleProviderSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string[]>,
  ) => {
    setSelectedProviderIds(event.target.value as string[]);
  };

  // Clean up polling interval on component unmount
  useEffect(() => {
    return () => {
      if (evaluationPollingInterval) {
        clearInterval(evaluationPollingInterval);
      }
    };
  }, [evaluationPollingInterval]);

  // Fetch providers when modal is opened
  useEffect(() => {
    if (evaluationModalOpen) {
      fetchProviders();
    }
  }, [evaluationModalOpen]);

  // Fetch providers on component mount
  useEffect(() => {
    fetchProviders();
  }, []);

  // Handle assertion creation and updates for a variable group
  const handleSaveAssertion = async (values: AssertionRequest) => {
    if (!dataset || !currentGroupId) return;

    setIsProcessing(true);
    try {
      if (isEditingAssertion && assertionToEdit?._id) {
        // For editing existing assertions
        await api.variableGroups.removeAssertion(
          dataset._id,
          currentGroupId,
          assertionToEdit._id,
        );
      }

      // Create the assertion directly in the variable group
      await api.variableGroups.addAssertionDirectly(
        dataset._id,
        currentGroupId,
        values,
      );

      // Refresh assertions
      const response = await api.variableGroups.getAssertions(
        dataset._id,
        currentGroupId,
      );

      if (response.data) {
        setAssertionsForGroup(response.data);
      }

      alert.success(
        isEditingAssertion
          ? "Assertion updated successfully"
          : "Assertion created successfully",
      );

      // Close the modal
      setIsCreatingAssertion(false);
      setIsEditingAssertion(false);
      setAssertionToEdit(null);
    } catch (error) {
      console.error("Error saving assertion:", error);
      alert.error("Failed to save assertion");
    } finally {
      setIsProcessing(false);
    }
  };

  // Execute all prompt tests for all datasets at once
  const executeAllPromptTests = async () => {
    if (!dataset || !selectedPrompt) {
      alert.error("A prompt must be linked to the dataset to run tests");
      return;
    }

    try {
      setExecutingAllTests(true);

      // Fetch all available providers
      let allProviderIds = [];
      try {
        const providersResponse = await api.providers.getAll();
        allProviderIds = providersResponse.data.map((provider) => provider._id);

        if (allProviderIds.length === 0) {
          alert.error("No providers available to run tests");
          setExecutingAllTests(false);
          return;
        }

        console.log(
          `Running tests with all ${allProviderIds.length} providers for all datasets`,
        );
      } catch (error) {
        console.error("Error fetching providers:", error);
        alert.error("Failed to fetch providers");
        setExecutingAllTests(false);
        return;
      }

      // Get all variable group IDs
      const variableGroupIds = dataset.variableGroups.map((group) => group._id);

      // Create a single combined evaluation
      const evaluationRequestData = {
        promptIds: [selectedPrompt._id],
        providerIds: allProviderIds,
        datasetId: dataset._id,
        variableGroupIds: variableGroupIds,
      };

      // Use the new combined evaluation endpoint
      const evaluationResponse = await api.evaluations.createCombined(
        evaluationRequestData,
      );
      const newEvaluationId = evaluationResponse.data._id;

      // Set the evaluation ID and open the modal
      setEvaluationId(newEvaluationId);
      setEvaluationModalOpen(true);
      startPollingEvaluation(newEvaluationId);

      alert.success("Started combined evaluation for all datasets");
    } catch (error) {
      console.error("Error executing all prompt tests:", error);
      alert.error("Failed to execute all prompt tests");
    } finally {
      setExecutingAllTests(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <PageHeader
        title={isNew ? "Create Prompt Test" : "Prompt Test Details"}
        subtitle={
          isNew
            ? "Create a new prompt test for testing prompts"
            : "Manage prompt test and its variables"
        }
        action={
          <Box sx={{ display: "flex", gap: 2 }}>
            {!isNew && selectedPrompt && dataset && (
              <LoadingButton
                variant="contained"
                color="primary"
                startIcon={<PlayArrowIcon />}
                onClick={executeAllPromptTests}
                loading={executingAllTests}
                disabled={!selectedPrompt || isNew || !dataset}
              >
                Execute All Tests
              </LoadingButton>
            )}
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate("/datasets")}
            >
              Back to Prompt Tests
            </Button>
          </Box>
        }
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Prompt Tests", path: "/datasets" },
          { label: isNew ? "Create" : dataset?.name || "Details", path: "" },
        ]}
      />

      <LoadingBackdrop open={loading && !dataset && !isNew} />

      {error && (
        <AlertMessage
          open={Boolean(error)}
          onClose={() => setError(null)}
          severity="error"
          title="Error"
          message={error}
        />
      )}

      {/* Dataset basic information form */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <Typography variant="h6" gutterBottom>
            Prompt Test Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Prompt Test Name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                variant="outlined"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={saving}
              startIcon={<SaveIcon />}
            >
              {isNew ? "Create Prompt Test" : "Update Prompt Test"}
            </LoadingButton>
          </Box>
        </Box>
      </Paper>

      {!isNew && dataset && (
        <>
          {/* Associated prompt section */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Associated Prompt
              </Typography>
              {selectedPrompt ? (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<UnlinkIcon />}
                  onClick={handlePromptUnlink}
                >
                  Unlink Prompt
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={handleOpenPromptDialog}
                >
                  Link a Prompt
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />

            {selectedPrompt ? (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mr: 2 }}
                  >
                    {selectedPrompt.name}
                  </Typography>
                  <Chip
                    label={selectedPrompt.type}
                    size="small"
                    color="primary"
                    sx={{ textTransform: "capitalize" }}
                  />
                  <Button
                    variant="text"
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={handleOpenPromptEditor}
                    startIcon={<CodeIcon />}
                  >
                    Edit Prompt
                  </Button>
                </Box>

                {selectedPrompt.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {selectedPrompt.description}
                  </Typography>
                )}

                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Variables in this prompt:
                </Typography>

                {templateVariables.length > 0 ? (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 1,
                        mb: 2,
                      }}
                    >
                      {templateVariables.map((variable) => (
                        <Chip
                          key={variable}
                          label={`{{${variable}}}`}
                          size="small"
                          color="info"
                          icon={<CodeIcon fontSize="small" />}
                        />
                      ))}
                    </Box>

                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddAllTemplateVariables}
                      sx={{ mt: 1 }}
                    >
                      Add All Variables as Group
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No template variables found in this prompt. Template
                    variables should be in format {"{{"} variable_name {"}}"}.
                  </Typography>
                )}
              </Box>
            ) : (
              <Alert severity="info">
                <AlertTitle>No prompt linked</AlertTitle>
                Link a prompt to this prompt test to identify template variables
                and create test cases.
              </Alert>
            )}
          </Paper>

          {/* Prompt Datasets */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Prompt Datasets
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setGroupNameInput("");
                  setGroupDescriptionInput("");
                  setPromptForTemplateGroup(true);
                }}
              >
                Create Prompt Dataset
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {dataset.variableGroups && dataset.variableGroups.length > 0 ? (
              <>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="prompt datasets tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {dataset.variableGroups.map((group) => (
                      <Tab
                        key={group._id}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <span>{group.name}</span>
                            <Chip
                              label={Object.keys(group.variables || {}).length}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                        }
                      />
                    ))}
                  </Tabs>
                </Box>

                {dataset.variableGroups.map((group, index) => (
                  <TabPanel key={group._id} value={tabValue} index={index}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Box>
                        <Typography variant="h5">{group.name}</Typography>
                        {group.description && (
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            {group.description}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Created: {formatDateTime(group.createdAt)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => handleEditGroup(group)}
                          color="primary"
                          title="Edit Group"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteGroup(group)}
                          color="error"
                          title="Delete Group"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Add prompt variable comparison section */}
                    {selectedPrompt && promptVarComparisons[group._id] && (
                      <Box sx={{ mb: 3 }}>
                        {(promptVarComparisons[group._id].missingInGroup
                          .length > 0 ||
                          promptVarComparisons[group._id].extraInGroup.length >
                            0) && (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              mb: 3,
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.02)",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Typography variant="subtitle1" color="primary">
                                Prompt Variable Sync Status
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() =>
                                  handleSyncGroupWithPrompt(group._id)
                                }
                                disabled={isProcessing}
                                startIcon={<SyncIcon />}
                              >
                                Sync All
                              </Button>
                            </Box>

                            {promptVarComparisons[group._id].missingInGroup
                              .length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  color="warning.main"
                                  gutterBottom
                                >
                                  Variables in prompt but missing from this
                                  dataset:
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {promptVarComparisons[
                                    group._id
                                  ].missingInGroup.map((varName) => (
                                    <Chip
                                      key={varName}
                                      label={varName}
                                      size="small"
                                      color="warning"
                                      icon={<AddIcon />}
                                      onClick={() =>
                                        handleAddMissingVariable(
                                          group._id,
                                          varName,
                                        )
                                      }
                                      sx={{ borderStyle: "dashed" }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {promptVarComparisons[group._id].extraInGroup
                              .length > 0 && (
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="error.main"
                                  gutterBottom
                                >
                                  Variables in this dataset but not used in
                                  prompt:
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {promptVarComparisons[
                                    group._id
                                  ].extraInGroup.map((varName) => (
                                    <Chip
                                      key={varName}
                                      label={varName}
                                      size="small"
                                      color="error"
                                      icon={<DeleteIcon />}
                                      onClick={() =>
                                        handleRemoveExtraVariable(
                                          group._id,
                                          varName,
                                        )
                                      }
                                      sx={{ textDecoration: "line-through" }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Paper>
                        )}

                        {promptVarComparisons[group._id].missingInGroup
                          .length === 0 &&
                          promptVarComparisons[group._id].extraInGroup
                            .length === 0 && (
                            <Alert severity="success" sx={{ mb: 3 }}>
                              <AlertTitle>In Sync</AlertTitle>
                              This prompt dataset is fully synchronized with the
                              prompt variables.
                            </Alert>
                          )}
                      </Box>
                    )}

                    {/* Display variables as a single list of key-value pairs */}
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6">Variables</Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            handleOpenGroupVariableDialog(group._id)
                          }
                        >
                          Add Variable
                        </Button>
                      </Box>

                      {/* Quick entry form */}
                      <Box sx={{ mb: 3 }}>
                        <TextField
                          label="New Variable Name"
                          value={variableNameInput}
                          onChange={(e) => setVariableNameInput(e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{ mr: 2, width: 250 }}
                          placeholder="Enter variable name"
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            handleAddVariableTypeToGroup(group._id)
                          }
                          disabled={!variableNameInput.trim() || isProcessing}
                        >
                          Quick Add
                        </Button>
                      </Box>

                      {Object.keys(group.variables || {}).length === 0 ? (
                        <Alert severity="info">
                          No variables added yet. Click the button above to add
                          one.
                        </Alert>
                      ) : (
                        <Grid container spacing={2}>
                          {Object.entries(group.variables).map(
                            ([varName, variable]) => (
                              <Grid item xs={12} md={6} lg={4} key={varName}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography
                                      variant="h6"
                                      component="div"
                                      gutterBottom
                                    >
                                      {varName}
                                    </Typography>
                                    {variable.description && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        paragraph
                                      >
                                        {variable.description}
                                      </Typography>
                                    )}
                                    <Typography
                                      variant="body1"
                                      color="text.primary"
                                      sx={{
                                        backgroundColor: "background.default",
                                        p: 1,
                                        borderRadius: 1,
                                        fontFamily: "monospace",
                                        maxHeight: "100px",
                                        overflow: "auto",
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {variable.value}
                                    </Typography>
                                  </CardContent>
                                  <CardActions
                                    sx={{ justifyContent: "flex-end" }}
                                  >
                                    <Button
                                      size="small"
                                      startIcon={<EditIcon />}
                                      onClick={() =>
                                        handleOpenGroupVariableDialog(
                                          group._id,
                                          varName,
                                        )
                                      }
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      startIcon={<DeleteIcon />}
                                      onClick={() =>
                                        handleDeleteGroupVariable(
                                          group._id,
                                          varName,
                                        )
                                      }
                                    >
                                      Delete
                                    </Button>
                                  </CardActions>
                                </Card>
                              </Grid>
                            ),
                          )}
                        </Grid>
                      )}
                    </Box>

                    {/* New Assertions Section */}
                    <Box sx={{ mt: 5 }}>
                      <Divider sx={{ mb: 3 }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 3,
                        }}
                      >
                        <Box>
                          <Typography variant="h6">Assertions</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Assertions are specific to this dataset and help
                            validate LLM responses
                          </Typography>
                        </Box>
                        <Box>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              // Load any existing assertions for this group
                              loadAssertionsForGroup(group._id);
                              setCurrentGroupId(group._id);
                              setIsCreatingAssertion(true);
                            }}
                          >
                            Create New Assertion
                          </Button>
                        </Box>
                      </Box>

                      {/* Display existing assertions for this group */}
                      <Box sx={{ mt: 2 }}>
                        {/* Existing assertions display */}
                        {assertionsForGroup.length === 0 ? (
                          <Alert severity="info" sx={{ mb: 3 }}>
                            No assertions have been added to this dataset yet.
                          </Alert>
                        ) : (
                          <Grid container spacing={2}>
                            {assertionsForGroup.map((assertion) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                                key={assertion._id}
                              >
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                    >
                                      {assertion.description ||
                                        `${getAssertionTypeLabel(assertion.type)} assertion`}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Type:{" "}
                                      {getAssertionTypeLabel(assertion.type)}
                                    </Typography>
                                    {/* Add any other assertion details you want to display */}
                                  </CardContent>
                                  <CardActions
                                    sx={{ justifyContent: "flex-end" }}
                                  >
                                    <Button
                                      size="small"
                                      startIcon={<EditIcon />}
                                      onClick={() => {
                                        setCurrentGroupId(group._id);
                                        handleEditAssertion(assertion);
                                      }}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      startIcon={<DeleteIcon />}
                                      onClick={() => {
                                        // Remove this assertion
                                        setSelectedGroupForTests(group._id);
                                        handleDeleteAssertion(assertion._id);
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </CardActions>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Box>
                    </Box>
                  </TabPanel>
                ))}
              </>
            ) : (
              <Alert severity="info">
                <AlertTitle>No prompt datasets</AlertTitle>
                Create a prompt dataset to organize your test variables.
                {templateVariables.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddAllTemplateVariables}
                    >
                      Add All Template Variables as Dataset
                    </Button>
                  </Box>
                )}
              </Alert>
            )}
          </Paper>

          {/* Legacy variables section with delete button */}
          {Object.keys(dataset.variables || {}).length > 0 && (
            <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }}>
              <Alert
                severity="info"
                action={
                  <Button
                    color="error"
                    size="small"
                    variant="outlined"
                    onClick={handleDeleteLegacyVariables}
                    disabled={isProcessing}
                  >
                    Delete Legacy Variables
                  </Button>
                }
              >
                <AlertTitle>Legacy Variables Detected</AlertTitle>
                Your prompt test contains individual variables from the previous
                version. We recommend moving these to prompt datasets for better
                organization. You can continue using the new prompt datasets
                system or remove the legacy variables.
              </Alert>
            </Paper>
          )}
        </>
      )}

      {/* Prompt dialog */}
      <Dialog
        open={promptDialogOpen}
        onClose={handleClosePromptDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isNew ? "Create Prompt Test" : "Select a Prompt"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {availablePrompts.map((prompt) => (
              <ListItem key={prompt._id}>
                <ListItemText
                  primary={prompt.name}
                  secondary={prompt.description}
                />
                <IconButton
                  edge="end"
                  onClick={() => handlePromptSelect(prompt._id)}
                >
                  <AddIcon />
                </IconButton>
              </ListItem>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePromptDialog} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Variable dialog for editing/adding variables */}
      <Dialog
        open={variableDialogOpen}
        onClose={handleCloseVariableDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedVariable
            ? `Edit ${selectedVariable.name} Variable`
            : `Add New Variable`}
        </DialogTitle>
        <form onSubmit={variableFormik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              id="name"
              name="name"
              label="Variable Name"
              value={variableFormik.values.name}
              onChange={variableFormik.handleChange}
              error={
                variableFormik.touched.name &&
                Boolean(variableFormik.errors.name)
              }
              helperText={
                variableFormik.touched.name && variableFormik.errors.name
              }
              required
            />
            <TextField
              fullWidth
              margin="normal"
              id="value"
              name="value"
              label="Value"
              multiline
              rows={4}
              value={variableFormik.values.value}
              onChange={variableFormik.handleChange}
              error={
                variableFormik.touched.value &&
                Boolean(variableFormik.errors.value)
              }
              helperText={
                variableFormik.touched.value && variableFormik.errors.value
              }
              required
            />
            <TextField
              fullWidth
              margin="normal"
              id="description"
              name="description"
              label="Description"
              multiline
              rows={2}
              value={variableFormik.values.description}
              onChange={variableFormik.handleChange}
              error={
                variableFormik.touched.description &&
                Boolean(variableFormik.errors.description)
              }
              helperText={
                variableFormik.touched.description &&
                variableFormik.errors.description
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseVariableDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <CircularProgress size={24} />
              ) : selectedVariable ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirm delete variable dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Variable"
        message={`Are you sure you want to delete this variable? This action cannot be undone.`}
        confirmButtonText="Delete"
        confirmButtonColor="error"
        onConfirm={confirmDeleteVariable}
        onCancel={() => setDeleteDialogOpen(false)}
        loading={isProcessing}
      />

      {/* Add this new dialog for creating variable groups */}
      <Dialog
        open={promptForTemplateGroup}
        onClose={() => setPromptForTemplateGroup(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Prompt Dataset</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Dataset Name"
              value={groupNameInput}
              onChange={(e) => setGroupNameInput(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={groupDescriptionInput}
              onChange={(e) => setGroupDescriptionInput(e.target.value)}
              margin="normal"
              multiline
              rows={2}
            />

            {templateVariables.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  This dataset will include the following variable types:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {templateVariables.map((variable) => (
                    <Chip
                      key={variable}
                      label={variable}
                      size="small"
                      color="primary"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptForTemplateGroup(false)}>
            Cancel
          </Button>
          <LoadingButton
            onClick={() =>
              handleCreateVariableGroup(groupNameInput, groupDescriptionInput)
            }
            loading={isProcessing}
            variant="contained"
            disabled={!groupNameInput.trim()}
          >
            Create Dataset
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Add the group edit dialog */}
      <Dialog
        open={groupEditDialogOpen}
        onClose={() => setGroupEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Prompt Dataset</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Dataset Name"
              value={groupNameInput}
              onChange={(e) => setGroupNameInput(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={groupDescriptionInput}
              onChange={(e) => setGroupDescriptionInput(e.target.value)}
              margin="normal"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupEditDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={handleUpdateGroup}
            loading={isProcessing}
            variant="contained"
            disabled={!groupNameInput.trim()}
          >
            Update Dataset
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Add the group delete confirmation dialog */}
      <ConfirmDialog
        open={groupDeleteDialogOpen}
        title="Delete Prompt Dataset"
        message={`Are you sure you want to delete this dataset and all its variables? This action cannot be undone.`}
        confirmButtonText="Delete"
        confirmButtonColor="error"
        onConfirm={confirmDeleteGroup}
        onCancel={() => setGroupDeleteDialogOpen(false)}
        loading={isProcessing}
      />

      {/* Dialog for variable changes affecting prompts */}
      <Dialog
        open={showPromptUpdateDialog}
        onClose={() => setShowPromptUpdateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Variable Changes Detected</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            You have made changes to variables that are used in one or more
            prompts:
          </Typography>

          {addedVariables.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Added Variables:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {addedVariables.map((v) => (
                  <Chip key={v} label={v} size="small" color="success" />
                ))}
              </Box>
            </Box>
          )}

          {removedVariables.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error.main" gutterBottom>
                Removed Variables:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {removedVariables.map((v) => (
                  <Chip key={v} label={v} size="small" color="error" />
                ))}
              </Box>
            </Box>
          )}

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Affected Prompts:
          </Typography>
          <List dense>
            {affectedPrompts.map((prompt) => (
              <ListItem key={prompt._id}>
                <ListItemText
                  primary={prompt.name}
                  secondary={prompt.description}
                />
              </ListItem>
            ))}
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Note</AlertTitle>
            If you proceed with updates, these prompts will remain unchanged.
            You may need to manually adjust them to reflect your variable
            changes.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptUpdateDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assertion Modal */}
      <AssertionModal
        open={isCreatingAssertion}
        onClose={handleCloseAssertionModal}
        initialData={assertionToEdit || undefined}
        isEdit={isEditingAssertion}
        onSubmit={handleSaveAssertion}
      />

      {/* Evaluation Modal */}
      <Dialog
        open={evaluationModalOpen}
        onClose={handleCloseEvaluationModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {evaluationId ? "Evaluation Results" : "Run Evaluation"}
        </DialogTitle>
        <DialogContent>
          {!evaluationId ? (
            // Provider selection view
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Select Providers
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Choose the AI providers to run this evaluation with
              </Typography>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="providers-select-label">Providers</InputLabel>
                <Select
                  labelId="providers-select-label"
                  id="providers-select"
                  multiple
                  value={selectedProviderIds}
                  onChange={handleProviderSelectionChange}
                  input={<OutlinedInput label="Providers" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const provider = providers.find((p) => p._id === value);
                        return (
                          <Chip key={value} label={provider?.name || value} />
                        );
                      })}
                    </Box>
                  )}
                >
                  {providers.map((provider) => (
                    <MenuItem key={provider._id} value={provider._id}>
                      <Checkbox
                        checked={selectedProviderIds.indexOf(provider._id) > -1}
                      />
                      <ListItemText
                        primary={provider.name}
                        secondary={provider.provider}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Evaluation Settings
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">Prompt</Typography>
                      <Typography variant="body2">
                        {selectedPrompt?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">Dataset Group</Typography>
                      <Typography variant="body2">
                        {dataset?.variableGroups[tabValue]?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Variables</Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        {dataset?.variableGroups[tabValue]?.variables &&
                          Object.keys(
                            dataset.variableGroups[tabValue].variables,
                          ).map((varName) => (
                            <Chip
                              key={varName}
                              label={varName}
                              size="small"
                              color="primary"
                            />
                          ))}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </Box>
          ) : // If we have evaluationData, show the EvaluationResultsModal when needed
          evaluationData && evaluationStatus === "completed" ? (
            <EvaluationResultsModal
              open={true}
              onClose={handleCloseEvaluationModal}
              evaluation={evaluationData}
            />
          ) : (
            // Evaluation results view
            <Box sx={{ mt: 2 }}>
              {(evaluationStatus === "pending" ||
                evaluationStatus === "running") && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" align="center" gutterBottom>
                    {evaluationStatus === "pending"
                      ? "Preparing Evaluation..."
                      : "Running Evaluation..."}
                  </Typography>
                  <LinearProgress sx={{ mt: 2 }} />
                </Box>
              )}

              {evaluationStatus === "failed" && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <AlertTitle>Evaluation Failed</AlertTitle>
                  {evaluationData?.error ||
                    "An error occurred while running the evaluation."}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!evaluationId ? (
            <>
              <Button onClick={handleCloseEvaluationModal}>Cancel</Button>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleExecuteEvaluation}
                loading={executingEvaluation}
                disabled={selectedProviderIds.length === 0}
              >
                Run Evaluation
              </LoadingButton>
            </>
          ) : (
            <>
              {evaluationStatus === "completed" && (
                <Button
                  onClick={() => {
                    if (evaluationData?._id) {
                      navigate(`/evaluations/${evaluationData._id}`);
                    }
                  }}
                >
                  View Full Results
                </Button>
              )}
              <Button onClick={handleCloseEvaluationModal}>Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog for prompt test editor */}
      <PromptEditorModal
        isRunningTests={isRunningTests}
        open={promptEditorOpen}
        handleRunPromptTests={handleRunPromptTests}
        onClose={() => setPromptEditorOpen(false)}
        prompt={editedPrompt}
        handlePromptChange={(value) => {
          setEditedPrompt({
            _id: editedPrompt?._id || "",
            name: editedPrompt?.name || "",
            prompt: value || "",
            type: editedPrompt?.type || "user",
            description: editedPrompt?.description || "",
          });
        }}
        testEvaluationData={testEvaluationData}
        testEvaluationStatus={testEvaluationStatus}
        handleSavePromptEdit={handleSavePromptEdit}
      />
    </Box>
  );
};

export default DatasetDetail;
